import { useLanguage } from "../../../../stores/LanguageStore";
import {
  Anchor,
  Button,
  Group,
  LoadingOverlay,
  Modal,
  Text,
} from "@mantine/core";
import { useCallback, useMemo, useState } from "react";
import { notifications } from "@mantine/notifications";
import BackButton from "../../../../components/View/BackButton";
import PRMStatusSelector from "../../../../components/Selectors/PRMStatusSelector";
import { updateProspectsStatus } from "../../../../api/PRMAPI";
import {
  useLeadsContainerStore,
  useLeadsStore,
} from "../../../../stores/LeadsStore";
import { useParams } from "react-router-dom";
import { useProfileStore } from "../../../../stores/UserStore";
import { IconCornerUpRight } from "@tabler/icons-react";
import { deselectAll } from "../utils";

const defaults = ["opener", "clicker", "answerer", "to_call", "out_of_office"];

const LeadsMover = () => {
  const lang = useLanguage((s) => s.language);
  const { userId } = useParams();
  const containers = useLeadsContainerStore((s) => s.containers);
  const setContainers = useLeadsContainerStore((s) => s.setContainers);
  const selectedIds = useLeadsContainerStore((s) => s.selectedIds);
  const setSelectedIds = useLeadsContainerStore((s) => s.setSelectedIds);
  const selectedColumns = useLeadsContainerStore((s) => s.selectedColumns);
  const unselectedIds = useLeadsContainerStore((s) => s.unselectedIds);
  const filter = useLeadsStore((s) => s.filter);
  const searchQuery = useLeadsStore((s) => s.query);
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(false);

  const getSelected = useCallback(() => {
    let final = 0;
    containers.forEach((container) => {
      if (selectedColumns.includes(container.id)) {
        let total_col = container.total;
        let total_unselected = unselectedIds[container.id]?.length || 0;

        final += total_col - total_unselected;
      } else {
        final += selectedIds[container.id]?.length || 0;
      }
    });

    return final;
  }, [containers, selectedColumns, unselectedIds, selectedIds]);

  const [status, setStatus] = useState(null);

  const handleFinish = useCallback(() => {
    setSelectedIds({});
    notifications.show({
      message: lang.prm.status.contacts.moved,
      color: "tertiary.8",
    });

    let newContainers = [
      ...containers.map((x) => {
        if (x.id?.toString() === status) {
          return { ...x, loading: true };
        }
        return x;
      }),
    ];
    // Delete individual IDS
    let selectedIdsColumns = Object.keys(selectedIds);
    selectedIdsColumns.forEach((c) => {
      let i = newContainers.findIndex((x) => x.id.toString() === c.toString());

      if (i > -1) newContainers[i].loading = true;
    });

    deselectAll()
    return setContainers(newContainers);
  }, [
    containers,
    lang.prm.status.contacts.moved,
    selectedIds,
    setContainers,
    setSelectedIds,
    status,
  ]);

  const handleMove = () => {
    let isCustom = !!parseInt(status);

    let mainFilterValues = [];
    let excluded = [];
    if (selectedColumns.length > 0) {
      selectedColumns.forEach((col) => {
        if (defaults.includes(col)) {
          let v = [
            {
              field_name: "status",
              type: "equals",
              value: col,
            },
            {
              field_name: "custom_status",
              type: "equals",
              value: "null",
            },
          ];

          if (searchQuery) {
            v.push({
              field_name: "any_datafield",
              type: "equals",
              value: searchQuery,
            });
          }

          if (filter) {
            v.push(filter);
          }

          mainFilterValues.push({
            mode: "and",
            values: v,
          });
        } else {
          let v = [
            {
              field_name: "custom_status",
              type: "equals",
              value: col.toString(),
            },
          ];

          if (filter) {
            v.push(filter);
          }

          mainFilterValues.push({
            mode: "and",
            values: v,
          });
        }
        if (unselectedIds[col]) {
          excluded.push(...unselectedIds[col]);
        }
      });
    }

    let parsedFilter = {
      mode: "or",
      values: mainFilterValues,
    };

    setLoading(true);
    updateProspectsStatus({
      user_id: parseInt(userId) || useProfileStore.getState().profile?.id,
      contacts_selection: {
        filter: parsedFilter,
        contact_ids: Object.values(selectedIds).flatMap((x) => x),
        excluded_contact_ids: excluded,
      },
      custom_status: isCustom ? parseInt(status) : null,
      status: isCustom ? "opener" : status,
    })
      .then((response) => {
        setOpened(false);
        handleFinish();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      {getSelected() > 0 && (
        <Anchor onClick={() => setOpened(true)} fw={600}>
          <Group gap={8}>
            <IconCornerUpRight size={16} />
            {lang.prm.status.contacts.move_label}
          </Group>
        </Anchor>
      )}

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={lang.prm.status.contacts.move_title}
        centered
      >
        <Text>
          {lang.prm.status.contacts.move_text.replace("#", getSelected())}
        </Text>

        <PRMStatusSelector
          value={status}
          onChange={setStatus}
          w={"100%"}
          size={"md"}
          required
          withinPortal={true}
          noLabel
        />

        <Group mt={"md"} justify="end">
          <BackButton onClick={() => setOpened(false)} />

          <Button
            color="red"
            onClick={handleMove}
            loading={loading}
            disabled={!status}
          >
            {lang.prm.status.contacts.move_submit}
          </Button>
        </Group>
      </Modal>
    </>
  );
};

export const LeadsMoverContent = ({
  selectedCount,
  handleClose,
  defaultStatus,
}) => {
  const lang = useLanguage((s) => s.language);
  const { userId } = useParams();
  const searchQuery = useLeadsStore((s) => s.query);
  const filter = useLeadsStore((s) => s.filter);
  const containers = useLeadsContainerStore((s) => s.containers);
  const setContainers = useLeadsContainerStore((s) => s.setContainers);
  const selectedIds = useLeadsContainerStore((s) => s.selectedIds);
  const setSelectedIds = useLeadsContainerStore((s) => s.setSelectedIds);
  const selectedColumns = useLeadsContainerStore((s) => s.selectedColumns);
  const unselectedIds = useLeadsContainerStore((s) => s.unselectedIds);

  const [status, setStatus] = useState(defaultStatus);
  const [loading, setLoading] = useState();

  const handleFinish = useCallback(() => {
    setSelectedIds({});
    notifications.show({
      message: lang.prm.status.contacts.moved,
      color: "tertiary.8",
    });

    let newContainers = [
      ...containers.map((x) => {
        if (x.id?.toString() === status) {
          return { ...x, loading: true };
        }
        return x;
      }),
    ];
    // Delete individual IDS
    let selectedIdsColumns = Object.keys(selectedIds);
    selectedIdsColumns.forEach((c) => {
      let i = newContainers.findIndex((x) => x.id.toString() === c.toString());

      if (i > -1) newContainers[i].loading = true;
    });

    setContainers(newContainers);
    return deselectAll(); 
  }, [
    containers,
    lang.prm.status.contacts.moved,
    selectedIds,
    setContainers,
    setSelectedIds,
    status,
  ]);

  const handleMove = () => {
    let isCustom = !!parseInt(status);

    let mainFilterValues = [];
    let excluded = [];
    if (selectedColumns.length > 0) {
      selectedColumns.forEach((col) => {
        if (defaults.includes(col)) {
          let v = [
            {
              field_name: "status",
              type: "equals",
              value: col,
            },
            {
              field_name: "custom_status",
              type: "equals",
              value: "null",
            },
          ];

          if (searchQuery) {
            v.push({
              field_name: "any_datafield",
              type: "equals",
              value: searchQuery,
            });
          }

          if (filter) {
            v.push(filter);
          }

          mainFilterValues.push({
            mode: "and",
            values: v,
          });
        } else {
          let v = [
            {
              field_name: "custom_status",
              type: "equals",
              value: col.toString(),
            },
          ];

          if (filter) {
            v.push(filter);
          }

          mainFilterValues.push({
            mode: "and",
            values: v,
          });
        }
        if (unselectedIds[col]) {
          excluded.push(...unselectedIds[col]);
        }
      });
    }

    let parsedFilter = {
      mode: "or",
      values: mainFilterValues,
    };

    setLoading(true);
    updateProspectsStatus({
      user_id: parseInt(userId) || useProfileStore.getState().profile?.id,
      contacts_selection: {
        filter: parsedFilter,
        contact_ids: Object.values(selectedIds).flatMap((x) => x),
        excluded_contact_ids: excluded,
      },
      custom_status: isCustom ? parseInt(status) : null,
      status: isCustom ? "opener" : status,
    })
      .then((response) => {
        setTimeout(() => {
          handleClose();
          handleFinish();
          setLoading(false);
        }, 1000);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <LoadingOverlay visible={loading} />

      <Text mb={8}>
        {lang.prm.status.contacts.move_text.split("#")[0]}
        <b>{selectedCount}</b>
        {lang.prm.status.contacts.move_text.split("#")[1]}
      </Text>

      <PRMStatusSelector
        value={status}
        onChange={setStatus}
        w={"100%"}
        size={"md"}
        required
        withinPortal={true}
        noLabel
      />

      <Group mt={"md"} justify="end">
        <BackButton onClick={handleClose} />

        <Button
          color="red"
          onClick={handleMove}
          loading={loading}
          disabled={!status}
        >
          {lang.prm.status.contacts.move_submit}
        </Button>
      </Group>
    </>
  );
};

export default LeadsMover;
